import { Prescription } from '../../interfaces/prescription';

export interface WhatsappMessageParams {
  patientFirstName: string;
  doctorName: string;
  validationCode: string;
  link: string;
  accessCode: string;
  medicineCount: number;
  examCount: number;
  medicalStatementCount: number;
  referralCount: number;
  medicalReportCount: number;
  formulaCount: number;
}

export function buildWhatsappMessageParams(
  prescription: Prescription,
  link: string,
  accessCode: string
): WhatsappMessageParams | null {
  const name = prescription.Paciente?.Nome;
  if (!name || !prescription.Prescritor) return null;

  return {
    patientFirstName: name.split(' ')[0],
    doctorName: prescription.Prescritor.Nome,
    validationCode: prescription.CodigoValidacao,
    link,
    accessCode,
    medicineCount: (prescription.Itens?.length || 0) + (prescription.ItensManuais?.length || 0),
    examCount: prescription.Exames?.length || 0,
    medicalStatementCount: prescription.Atestados?.length || 0,
    referralCount: prescription.Encaminhamentos?.length || 0,
    medicalReportCount: prescription.Relatorios?.length || 0,
    formulaCount: prescription.Formulas?.length || 0,
  };
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function buildStandardMessage(
  patientFirstName: string,
  documentLabel: string,
  doctorName: string,
  link: string,
  validationCode: string,
  accessCode: string
): string {
  return `Olá ${patientFirstName}, ${documentLabel} de Dr(a) ${doctorName} está disponível.\n\nAcesse pelo link:\n${link}\n\nCódigo da prescrição: *${validationCode}*\nCódigo de acesso: *${accessCode}*`;
}

function buildStandardMessagePlural(
  patientFirstName: string,
  documentLabel: string,
  doctorName: string,
  link: string,
  validationCode: string,
  accessCode: string
): string {
  return `Olá ${patientFirstName}, ${documentLabel} de Dr(a) ${doctorName} estão disponíveis.\n\nAcesse pelo link:\n${link}\n\nCódigo da prescrição: *${validationCode}*\nCódigo de acesso: *${accessCode}*`;
}

export function buildWhatsappMessage(params: WhatsappMessageParams): string {
  const {
    patientFirstName,
    doctorName,
    validationCode,
    link,
    accessCode,
    medicineCount,
    examCount,
    medicalStatementCount,
    referralCount,
    medicalReportCount,
    formulaCount,
  } = params;

  const documentTypeCount = [
    medicineCount > 0,
    examCount > 0,
    medicalStatementCount > 0,
    referralCount > 0,
    medicalReportCount > 0,
    formulaCount > 0,
  ].filter(Boolean).length;

  // Múltiplos tipos de documento
  if (documentTypeCount >= 2) {
    return buildStandardMessagePlural(
      patientFirstName,
      'seus documentos médicos',
      doctorName,
      link,
      validationCode,
      accessCode
    );
  }

  // Receita (mantém formato original com instruções de compra)
  if (medicineCount > 0) {
    const label = pluralize(medicineCount, 'uma receita médica', 'receitas médicas');
    return `Olá ${patientFirstName}, você recebeu ${label} do Dr(a) ${doctorName}!\n\nAcesse sua receita digital Mevo através do link:\n${link}\nPara comprar com sua receita digital:\n1 - Informe à farmácia que possui uma receita digital Mevo;\n2 - Informe o *código da sua receita ${validationCode}*\n3 - Seu código de acesso é *${accessCode}*`;
  }

  // Exame
  if (examCount > 0) {
    const label = pluralize(examCount, 'sua solicitação de exame', 'suas solicitações de exames');
    return examCount === 1
      ? buildStandardMessage(patientFirstName, label, doctorName, link, validationCode, accessCode)
      : buildStandardMessagePlural(patientFirstName, label, doctorName, link, validationCode, accessCode);
  }

  // Atestado
  if (medicalStatementCount > 0) {
    const label = pluralize(medicalStatementCount, 'seu atestado', 'seus atestados');
    return medicalStatementCount === 1
      ? buildStandardMessage(patientFirstName, label, doctorName, link, validationCode, accessCode)
      : buildStandardMessagePlural(patientFirstName, label, doctorName, link, validationCode, accessCode);
  }

  // Encaminhamento
  if (referralCount > 0) {
    const label = pluralize(referralCount, 'seu encaminhamento médico', 'seus encaminhamentos médicos');
    return referralCount === 1
      ? buildStandardMessage(patientFirstName, label, doctorName, link, validationCode, accessCode)
      : buildStandardMessagePlural(patientFirstName, label, doctorName, link, validationCode, accessCode);
  }

  // Relatório
  if (medicalReportCount > 0) {
    const label = pluralize(medicalReportCount, 'seu relatório médico', 'seus relatórios médicos');
    return medicalReportCount === 1
      ? buildStandardMessage(patientFirstName, label, doctorName, link, validationCode, accessCode)
      : buildStandardMessagePlural(patientFirstName, label, doctorName, link, validationCode, accessCode);
  }

  // Fórmula manipulada
  if (formulaCount > 0) {
    const label = pluralize(formulaCount, 'sua fórmula manipulada', 'suas fórmulas manipuladas');
    return formulaCount === 1
      ? buildStandardMessage(patientFirstName, label, doctorName, link, validationCode, accessCode)
      : buildStandardMessagePlural(patientFirstName, label, doctorName, link, validationCode, accessCode);
  }

  // Fallback
  return buildStandardMessagePlural(
    patientFirstName,
    'seus documentos médicos',
    doctorName,
    link,
    validationCode,
    accessCode
  );
}
